import React from "react";
import { fmtNum, positionColor } from "@/lib/format";
import { Users, Star, ChevronRight } from "lucide-react";
import { SignInRequired } from "@/components/SignInRequired";

export function LeaguesPanel({ embedded = false, isGuest = false, onSignIn, user, leagues, selectedLeagueId, onSelectLeague, leagueDetail, onLoadTeam, onAddToSide }) {
  return (
    <section className={embedded ? "" : "mt-16"} data-testid="leagues-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">03 / Leagues</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">Your league.</h2>
          <p className="text-sm text-[#52525B] mt-2">
            Side A defaults to your team. Load any opponent into Side B with one click — or just search a player and the side auto-fills with that player's team.
          </p>
        </div>
      </div>

      {isGuest ? (
        <SignInRequired
          description="Your leagues and rosters are tied to your Sleeper account. Sign in with your username to load them here."
          onSignIn={onSignIn}
          testId="guest-league-cta"
        />
      ) : leagues.length === 0 ? (
        <div className="brutal-card p-6 text-sm text-[#52525B]" data-testid="no-leagues">
          No Sleeper leagues found for the current season under <span className="font-mono-data text-[#0A0A0A]">{user.username}</span>.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="league-grid">
            {leagues.map((lg) => (
              <button
                key={lg.league_id}
                onClick={() => onSelectLeague(lg.league_id)}
                className={`brutal-card p-3 text-left transition-colors ${selectedLeagueId === lg.league_id ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F3F4F6]"}`}
                data-testid={`league-card-${lg.league_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="label-eyebrow opacity-70">{lg.season}</span>
                  {selectedLeagueId === lg.league_id && <ChevronRight size={12} />}
                </div>
                <div className="font-display font-bold text-sm mt-1 tracking-tight truncate">{lg.name}</div>
                <div className="text-[10px] mt-1 font-mono-data flex items-center gap-1 opacity-70">
                  <Users size={10} /> {lg.total_rosters}
                </div>
              </button>
            ))}
          </div>

          {leagueDetail && (
            <div className="brutal-card" data-testid="league-detail">
              <div className="border-b border-[#0A0A0A] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="label-eyebrow">Rosters · sorted by value</div>
                  <div className="font-display font-bold text-xl">{leagueDetail.name}</div>
                </div>
                <div className="text-xs text-[#52525B] font-mono-data uppercase tracking-widest">
                  Format · {leagueDetail.format === "superflex" ? "Superflex" : "1QB"}
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[#0A0A0A]/10">
                {leagueDetail.rosters.map((r) => (
                  <RosterCard key={r.roster_id} roster={r} onLoadTeam={onLoadTeam} onAddToSide={onAddToSide} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RosterCard({ roster, onLoadTeam, onAddToSide }) {
  const sorted = [...roster.players].sort((a, b) => (b.combinedValue || 0) - (a.combinedValue || 0));
  return (
    <div className="p-4" data-testid={`roster-${roster.roster_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-base truncate">{roster.team_name}</span>
            {roster.is_me && <Star size={12} className="text-[#7C3AED] fill-[#7C3AED]" />}
          </div>
          <div className="text-[11px] text-[#52525B] font-mono-data">
            {roster.display_name} · {roster.wins}-{roster.losses} · {roster.players.length} assets
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="label-eyebrow">Total</div>
            <div className="font-mono-data font-semibold tabular-nums">{fmtNum(roster.total_value)}</div>
          </div>
          <div className="flex flex-col gap-1">
            <button onClick={() => onLoadTeam("A", roster)} className="text-[10px] uppercase tracking-widest border border-[#0A0A0A]/40 hover:bg-[#0A0A0A] hover:text-white px-2 py-1 transition-colors" data-testid={`load-a-${roster.roster_id}`}>→ Side A</button>
            <button onClick={() => onLoadTeam("B", roster)} className="text-[10px] uppercase tracking-widest border border-[#0A0A0A]/40 hover:bg-[#0A0A0A] hover:text-white px-2 py-1 transition-colors" data-testid={`load-b-${roster.roster_id}`}>→ Side B</button>
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-1 max-h-[260px] overflow-auto pr-1">
        {sorted.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 px-2 py-1.5 border border-[#0A0A0A]/10 hover:border-[#0A0A0A] transition-colors text-sm">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(p.position)}`}>{p.position}</span>
              <span className="truncate">{p.name}</span>
            </div>
            <span className="font-mono-data text-xs tabular-nums text-[#52525B]">{fmtNum(p.combinedValue)}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => onAddToSide("A", p)} className="text-[10px] uppercase tracking-widest border border-[#0A0A0A]/40 hover:bg-[#0A0A0A] hover:text-white px-1.5 py-0.5 transition-colors" data-testid={`add-a-${p.id}`}>A</button>
              <button onClick={() => onAddToSide("B", p)} className="text-[10px] uppercase tracking-widest border border-[#0A0A0A]/40 hover:bg-[#0A0A0A] hover:text-white px-1.5 py-0.5 transition-colors" data-testid={`add-b-${p.id}`}>B</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
